/**
 * MSW Evaluation Note — static options + empty form state.
 * Common header (1–6) lives in mswNotesCommonHeaderUtils / PtNotesCommonHeader.
 * Fields below are MSW Evaluation–specific clinical sections.
 */

import { emptyMswCommonHeader } from "./mswNotesCommonHeaderUtils";

export const PERSONS_PRESENT_OPTIONS = [
  "Pt",
  "Spouse",
  "CG",
  "Dtr",
  "Dtr-in-law",
  "Son",
  "Son-in-law",
  "Neighbor",
];

export const LIVING_ARRANGEMENT_OPTIONS = [
  "Alone",
  "With Spouse",
  "With Other Family",
  "With Friend",
  "Other",
];

export const LIVING_VENUE_OPTIONS = [
  "House",
  "Apartment",
  "ALF",
  "SNF",
  "Other",
];

export const CARE_MANAGEMENT_OPTIONS = [
  "Independent",
  "Requires assistance",
  "Requires total care",
  "Caregiver available",
  "Other",
];

export const LIVING_CONDITION_OPTIONS = [
  "Safe/Adequate",
  "Clean",
  "Dirty",
  "Cluttered",
  "Hoarding",
];

export const MEALS_PROVIDER_OPTIONS = [
  "Has/Self",
  "CG/Family/Friend",
  "Meals on Wheels (MOW)",
  "Project Angel Food",
];

export const TRANSPORTATION_OPTIONS = [
  "Has Vehicle",
  "CG/Family/Friend",
  "Access Services",
  "Dial a Ride / Cityride",
];

export const HEALTH_INSURANCE_OPTIONS = [
  "M-Care",
  "M-Cal",
  "Private",
  "VA Benefit",
];

export const MENTAL_STATUS_OPTIONS = [
  "Alert",
  "Oriented x3",
  "Forgetful",
  "Confused",
];

export const EMOTIONAL_PRESENTATION_OPTIONS = [
  "Anxious",
  "Depressed",
  "Labile",
  "Agitated",
];

export const PT_COPING_SKILLS_OPTIONS = [
  "Adequate",
  "Limited",
  "Poor",
  "N/A",
];

export const RESOURCE_STATUS_OPTIONS = [
  "Educated",
  "Refused",
  "Accepted",
  "Pending",
  "N/A",
];

export const RESOURCE_STATUS_ROWS = [
  { key: "lifeline", label: "Lifeline" },
  { key: "vaServices", label: "VA Services" },
  { key: "alfPlacement", label: "ALF Placement" },
];

export const ADDITIONAL_CRS_LTC_OPTIONS = [
  "BCF/ALF Placement",
  "ADHC",
  "Alzheimer's Association",
  "Social Security Administration",
  "SNF",
  "DHCS-Medi-Cal",
  "Legal Aid",
  "DPOA / DNR / POLST",
  "Lifeline",
  "Veterans Administration",
  "Parkinson's Association",
  "Respite Care",
  "EDD-State Disability",
  "Senior center/Case management",
  "Housing",
  "Other",
];

export const FUTURE_PLAN_OPTIONS = ["FUHV", "FUTC"];

export const MSW_EVAL_ARRAY_KEYS = [
  "personsPresent",
  "livingCondition",
  "mealsProvider",
  "transportation",
  "healthInsurance",
  "mentalStatus",
  "emotionalPresentation",
  "additionalCrsLtc",
  "futurePlan",
];

export const MSW_EVAL_TEXT_LIST_KEYS = [
  "problems",
  "goals",
  "interventions",
  "outcomes",
];

const newItemId = (prefix = "item") =>
  `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

export const emptyTextItem = (n = 1, kind = "item") => ({
  id: newItemId(`${kind}-${n}`),
  text: "",
});

export const emptyTextItems = (count = 1, kind = "item") =>
  Array.from({ length: Math.max(1, count) }, (_, i) =>
    emptyTextItem(i + 1, kind),
  );

export const renumberTextItems = (rows, kind = "item") =>
  (Array.isArray(rows) ? rows : []).map((row, idx) => ({
    id: row?.id || newItemId(`${kind}-${idx + 1}`),
    text: typeof row === "string" ? row : row?.text || "",
  }));

export const normalizeTextItems = (rows, { kind = "item", minCount = 1 } = {}) => {
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) return emptyTextItems(minCount, kind);
  return renumberTextItems(list, kind);
};

export const serializeTextItems = (rows) =>
  renumberTextItems(rows).map(({ id, text }) => ({ id, text: text || "" }));

export const addTextItem = (rows, kind = "item") => {
  const current = Array.isArray(rows) ? rows : [];
  return renumberTextItems(
    [...current, emptyTextItem(current.length + 1, kind)],
    kind,
  );
};

export const removeTextItem = (rows, itemId, kind = "item", { minCount = 1 } = {}) => {
  const current = Array.isArray(rows) ? rows : [];
  if (current.length <= minCount) return current;
  return renumberTextItems(
    current.filter((row) => row.id !== itemId),
    kind,
  );
};

export const emptyResourceStatuses = () => ({
  lifeline: "",
  vaServices: "",
  alfPlacement: "",
});

export const normalizeResourceStatuses = (value) => {
  const src = value && typeof value === "object" ? value : {};
  return {
    lifeline: String(src.lifeline || ""),
    vaServices: String(src.vaServices || ""),
    alfPlacement: String(src.alfPlacement || ""),
  };
};

export const toggleInList = (list, item) => {
  const current = Array.isArray(list) ? list : [];
  return current.includes(item)
    ? current.filter((x) => x !== item)
    : [...current, item];
};

const asStringArray = (value) =>
  Array.isArray(value) ? value.map(String).filter(Boolean) : [];

/**
 * Pull MSW clinical fields that used to live under commonHeader (legacy drafts)
 * into top-level payload keys.
 */
export const migrateLegacyMswEvalFields = (payload = {}) => {
  const src = payload && typeof payload === "object" ? payload : {};
  const legacy =
    src.commonHeader && typeof src.commonHeader === "object"
      ? src.commonHeader
      : {};

  const pickArr = (key) => {
    const top = asStringArray(src[key]);
    if (top.length) return top;
    return asStringArray(legacy[key]);
  };

  const pickScalar = (key, fallback = "") => {
    const top = String(src[key] ?? "").trim();
    if (top) return top;
    return String(legacy[key] ?? fallback).trim() || fallback;
  };

  return {
    personsPresent: pickArr("personsPresent"),
    livingArrangement:
      pickScalar("livingArrangement") || "With Other Family",
    livingVenue: pickScalar("livingVenue"),
    careManagement: pickScalar("careManagement"),
    livingCondition: pickArr("livingCondition"),
    mealsProvider: pickArr("mealsProvider"),
    transportation: pickArr("transportation"),
  };
};

export const emptyMswEvalPayload = () => ({
  commonHeader: emptyMswCommonHeader(),
  personsPresent: [],
  livingArrangement: "With Other Family",
  livingVenue: "",
  careManagement: "",
  livingCondition: [],
  mealsProvider: [],
  transportation: [],
  problems: emptyTextItems(1, "problem"),
  goals: emptyTextItems(1, "goal"),
  interventions: emptyTextItems(1, "intervention"),
  outcomes: emptyTextItems(1, "outcome"),
  monthlyIncome: "",
  healthInsurance: [],
  mentalStatus: [],
  emotionalPresentation: [],
  ptCopingSkills: "",
  resourceStatuses: emptyResourceStatuses(),
  additionalCrsLtc: [],
  futurePlan: [],
  frequencyDuration: "",
  billingCode: "",
  timeIn: "",
  timeOut: "",
});
